/**
 * AircraftForm - Unified aircraft form component
 *
 * Supports three variants:
 * - full: All fields with collapsible sections (for drawer)
 * - details: Basic info + dimensions + compliance (for quick edit)
 * - notes: Just notes field (for quick edit)
 */

import { useEffect, useCallback, useRef } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { useCreateAircraft, useUpdateAircraft } from '@/hooks/useAircraft'
import {
  aircraftFormSchema,
  aircraftDetailsSchema,
  aircraftNotesSchema,
  defaultAircraftFormValues,
  type AircraftFormValues,
  type AircraftDetailsValues,
  type AircraftNotesValues,
} from './aircraft-form-schema'
import {
  BasicInfoFields,
  OwnershipFields,
  DimensionsFields,
  ComplianceFields,
  LocationFields,
  NotesField,
} from './fields'
import type { AircraftFormData } from '@/types/aircrafts'
import { useState } from 'react'

type FormVariant = 'full' | 'details' | 'notes'

interface AircraftFormProps {
  /** Form variant determines which fields are shown */
  variant: FormVariant
  /** Aircraft ID for edit mode (undefined = create mode) */
  aircraftId?: string
  /** Initial form data (snake_case to match schema) */
  initialData?: Partial<AircraftFormValues>
  /** Field to auto-focus on mount */
  prefocusField?: string
  /** Callback when form is successfully submitted */
  onSuccess?: () => void
  /** Callback when cancel button is clicked */
  onCancel?: () => void
  /** Callback when form dirty state changes */
  onDirtyChange?: (isDirty: boolean) => void
  /** External submit button ID (for drawer footer) */
  formId?: string
  /** Hide action buttons (for external submit) */
  hideButtons?: boolean
}

/**
 * Field name mapping from common names to form field names
 */
const FIELD_NAME_MAP: Record<string, keyof AircraftFormValues> = {
  // camelCase to snake_case mappings for prefocus
  insuranceExpiry: 'insurance_expiry',
  annualInspectionDue: 'annual_inspection_due',
  serialNumber: 'serial_number',
  yearManufactured: 'year_manufactured',
  manufacturer: 'manufacturer',
  model: 'model',
  notes: 'notes',
}

export function AircraftForm({
  variant,
  aircraftId,
  initialData,
  prefocusField,
  onSuccess,
  onCancel,
  onDirtyChange,
  formId = 'aircraft-form',
  hideButtons = false,
}: AircraftFormProps) {
  const isEdit = !!aircraftId

  // Collapsible states (only for full variant)
  const [ownershipOpen, setOwnershipOpen] = useState(true)
  const [dimensionsOpen, setDimensionsOpen] = useState(false)
  const [complianceOpen, setComplianceOpen] = useState(false)
  const [locationOpen, setLocationOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)

  // Select schema based on variant
  const schema = variant === 'full'
    ? aircraftFormSchema
    : variant === 'details'
    ? aircraftDetailsSchema
    : aircraftNotesSchema

  // Mutations
  const createAircraft = useCreateAircraft()
  const updateAircraft = useUpdateAircraft()
  const isMutating = createAircraft.isPending || updateAircraft.isPending

  // Initialize form with appropriate defaults
  const form = useForm<AircraftFormValues>({
    // The form always carries the full AircraftFormValues shape, but `schema` is a
    // narrower picked schema for the 'details'/'notes' variants. TS can't express
    // "this resolver validates a subset of the form values", hence the cast.
    resolver: zodResolver(schema) as unknown as Resolver<AircraftFormValues>,
    defaultValues: {
      ...defaultAircraftFormValues,
      ...initialData,
    },
  })

  const { isDirty } = form.formState

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Reset form when initialData values actually change (for edit mode)
  const initialDataRef = useRef<string>('')

  useEffect(() => {
    if (initialData) {
      const key = JSON.stringify(initialData)
      if (key !== initialDataRef.current) {
        initialDataRef.current = key
        form.reset({
          ...defaultAircraftFormValues,
          ...initialData,
        })
      }
    }
  }, [initialData, form])

  // Auto-focus the specified field
  useEffect(() => {
    if (prefocusField) {
      const fieldName = FIELD_NAME_MAP[prefocusField] || prefocusField as keyof AircraftFormValues
      setTimeout(() => form.setFocus(fieldName), 100)
    }
  }, [prefocusField, form])

  // Handle form submission
  const onSubmit = useCallback(async (values: AircraftFormValues | AircraftDetailsValues | AircraftNotesValues) => {
    try {
      if (variant === 'full') {
        // Full form - all fields
        const fullValues = values as AircraftFormValues
        const formData: AircraftFormData = {
          ...fullValues,
          tail_number: fullValues.tail_number.toUpperCase(),
        }

        if (isEdit && aircraftId) {
          await updateAircraft.mutateAsync({ id: aircraftId, data: formData })
        } else {
          await createAircraft.mutateAsync(formData)
        }
      } else {
        // Partial form - only submit dirty fields
        if (isEdit && aircraftId) {
          // Build partial update with only the fields in this variant
          const partialData: Partial<AircraftFormData> = {}

          if (variant === 'details') {
            const detailsValues = values as AircraftDetailsValues
            partialData.manufacturer = detailsValues.manufacturer
            partialData.model = detailsValues.model
            partialData.serial_number = detailsValues.serial_number
            partialData.year_manufactured = detailsValues.year_manufactured
            partialData.wingspan = detailsValues.wingspan
            partialData.length = detailsValues.length
            partialData.height = detailsValues.height
            partialData.insurance_expiry = detailsValues.insurance_expiry
            partialData.annual_inspection_due = detailsValues.annual_inspection_due
          } else if (variant === 'notes') {
            const notesValues = values as AircraftNotesValues
            partialData.notes = notesValues.notes
          }

          await updateAircraft.mutateAsync({ id: aircraftId, data: partialData as AircraftFormData })
        }
      }

      form.reset()
      onSuccess?.()
    } catch (error) {
      console.error('Error saving aircraft:', error)
    }
  }, [variant, isEdit, aircraftId, createAircraft, updateAircraft, form, onSuccess])

  // Render collapsible section header (for full variant)
  const renderSectionHeader = (
    title: string,
    isOpen: boolean,
    onToggle: (open: boolean) => void
  ) => (
    <CollapsibleTrigger asChild>
      <Button
        variant="ghost"
        className="w-full justify-between p-0 h-auto hover:bg-transparent"
        onClick={() => onToggle(!isOpen)}
      >
        <h3 className="text-sm font-medium">{title}</h3>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </Button>
    </CollapsibleTrigger>
  )

  // Render full form with collapsible sections
  const renderFullForm = () => (
    <>
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Basic Information</h3>
        <BasicInfoFields form={form} disabled={isMutating} />
      </div>

      <Separator />

      {/* Ownership (Collapsible) */}
      <Collapsible open={ownershipOpen} onOpenChange={setOwnershipOpen}>
        {renderSectionHeader('Ownership', ownershipOpen, setOwnershipOpen)}
        <CollapsibleContent className="pt-4">
          <OwnershipFields form={form} disabled={isMutating} />
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* Dimensions (Collapsible) */}
      <Collapsible open={dimensionsOpen} onOpenChange={setDimensionsOpen}>
        {renderSectionHeader('Dimensions & Weight', dimensionsOpen, setDimensionsOpen)}
        <CollapsibleContent className="pt-4">
          <DimensionsFields form={form} disabled={isMutating} />
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* Compliance (Collapsible) */}
      <Collapsible open={complianceOpen} onOpenChange={setComplianceOpen}>
        {renderSectionHeader('Registration & Compliance', complianceOpen, setComplianceOpen)}
        <CollapsibleContent className="pt-4">
          <ComplianceFields form={form} disabled={isMutating} />
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* Location (Collapsible) */}
      <Collapsible open={locationOpen} onOpenChange={setLocationOpen}>
        {renderSectionHeader('Location', locationOpen, setLocationOpen)}
        <CollapsibleContent className="pt-4">
          <LocationFields form={form} disabled={isMutating} />
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* Notes (Collapsible) */}
      <Collapsible open={notesOpen} onOpenChange={setNotesOpen}>
        {renderSectionHeader('Notes', notesOpen, setNotesOpen)}
        <CollapsibleContent className="pt-4">
          <NotesField form={form} disabled={isMutating} showLabel={false} showDescription={false} />
        </CollapsibleContent>
      </Collapsible>
    </>
  )

  // Render details form (expanded, no collapsible)
  const renderDetailsForm = () => (
    <>
      {/* Aircraft Info */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Aircraft Info</h4>
        <BasicInfoFields
          form={form}
          disabled={isMutating}
          includeTailNumber={false}
          includeStatus={false}
          includeAircraftType={false}
        />
      </div>

      {/* Dimensions */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Dimensions (ft)</h4>
        <DimensionsFields
          form={form}
          disabled={isMutating}
          includeWeight={false}
          includeTailHeight={false}
        />
      </div>

      {/* Compliance */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Compliance</h4>
        <ComplianceFields form={form} disabled={isMutating} includeCountry={false} />
      </div>
    </>
  )

  // Render notes form (just textarea)
  const renderNotesForm = () => (
    <NotesField form={form} disabled={isMutating} />
  )

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        {variant === 'full' && renderFullForm()}
        {variant === 'details' && renderDetailsForm()}
        {variant === 'notes' && renderNotesForm()}

        {!hideButtons && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={isMutating}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isMutating}>
              {isMutating ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}

/**
 * Helper to convert AircraftDetailInfo (camelCase) to form values (snake_case)
 */
export function mapAircraftDetailToFormValues(
  aircraft: {
    manufacturer?: string | null
    model?: string | null
    serialNumber?: string | null
    yearManufactured?: number | null
    wingspan?: number | null
    length?: number | null
    height?: number | null
    insuranceExpiry?: string | null
    annualInspectionDue?: string | null
    notes?: string | null
  }
): Partial<AircraftFormValues> {
  return {
    manufacturer: aircraft.manufacturer ?? null,
    model: aircraft.model ?? null,
    serial_number: aircraft.serialNumber ?? null,
    year_manufactured: aircraft.yearManufactured ?? null,
    wingspan: aircraft.wingspan ?? null,
    length: aircraft.length ?? null,
    height: aircraft.height ?? null,
    insurance_expiry: aircraft.insuranceExpiry ?? null,
    annual_inspection_due: aircraft.annualInspectionDue ?? null,
    notes: aircraft.notes ?? null,
  }
}
